import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import PaginationBar from '../../components/PaginationBar';
import SortableHeader from '../../components/SortableHeader';
import { sortRows } from '../../utils/sort';
import useUrlPagination from '../../utils/useUrlPagination';

const MODULE_LABELS = {
    borang5: 'Borang 5',
    laporan_tindakan: 'Laporan Tindakan',
    laporan_tindakan_kes: 'Laporan Tindakan (Kes)',
    iwaran_dispatch: 'i-Waran (Edaran Daerah)',
    iwaran_court: 'i-Waran (Mahkamah)',
};

const STATUS_STYLES = {
    sent: { background: '#e6f6ec', color: '#1a7f45', border: '1px solid #b6e3c7' },
    failed: { background: '#fdeaea', color: '#b02a2a', border: '1px solid #f3b4b4' },
    skipped: { background: '#fff6e0', color: '#8a6400', border: '1px solid #ecd089' },
};

const STATUS_LABELS = { sent: 'Berjaya', failed: 'Gagal', skipped: 'Dilangkau' };

const moduleLabel = (key) => MODULE_LABELS[key] || key || '-';

const EmailDeliveryLog = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [moduleFilter, setModuleFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [moduleOptions, setModuleOptions] = useState([]);
    const [summary, setSummary] = useState({ sent: 0, failed: 0, skipped: 0, total: 0 });
    const [selected, setSelected] = useState(null);
    const { page, setPage, perPage, setPerPage } = useUrlPagination(15);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [sortKey, setSortKey] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    const loadLogs = () => {
        if (!apiUrl) {
            setError('API URL tidak diset.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        axios.get(`${apiUrl}/admin/email-logs`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params: {
                page,
                per_page: perPage,
                keyword: keyword.trim() || undefined,
                status: statusFilter || undefined,
                module: moduleFilter || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
            },
        })
            .then((response) => {
                setRows(response?.data?.data || []);
                setTotal(response?.data?.meta?.total ?? 0);
                setLastPage(response?.data?.meta?.last_page ?? 1);
                setModuleOptions(response?.data?.filters?.modules || []);
                setSummary(response?.data?.summary || { sent: 0, failed: 0, skipped: 0, total: 0 });
                setError('');
            })
            .catch((err) => {
                setError(err?.response?.data?.message || 'Gagal memuatkan log e-mel.');
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, page, perPage, statusFilter, moduleFilter, dateFrom, dateTo]);

    const keywordEffectReady = useRef(false);
    useEffect(() => {
        if (!keywordEffectReady.current) {
            keywordEffectReady.current = true;
            return undefined;
        }
        const timer = setTimeout(() => {
            setPage(1);
            loadLogs();
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword]);

    const clearFilters = () => {
        setKeyword('');
        setStatusFilter('');
        setModuleFilter('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    const startIndex = total === 0 ? 0 : (page - 1) * perPage + 1;
    const endIndex = Math.min(page * perPage, total);

    const sortColumns = [
        { key: 'created_at', label: 'Tarikh', sortable: true },
        { key: 'module', label: 'Modul', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'reference_no', label: 'Rujukan Kes', sortable: true },
        { key: 'to', label: 'Penerima', sortable: false },
        { key: 'subject', label: 'Subjek', sortable: false },
        { key: '', label: '', sortable: false },
    ];

    const sortAccessors = useMemo(() => ({
        created_at: (item) => item.created_at || '',
        module: (item) => item.module || '',
        status: (item) => item.status || '',
        reference_no: (item) => item.reference_no || '',
    }), []);

    const sortedRows = useMemo(
        () => sortRows(rows, sortKey, sortDir, sortAccessors),
        [rows, sortKey, sortDir, sortAccessors]
    );

    const handleSort = (key) => {
        if (!key) return;
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const statusBadge = (status) => (
        <span
            className="app-badge"
            style={{ ...(STATUS_STYLES[status] || {}), fontWeight: 600 }}
        >
            {STATUS_LABELS[status] || status}
        </span>
    );

    return (
        <div className="app-complaints app-email-log">
            <div className="app-complaints-header">
                <div>
                    <span className="app-eyebrow">Pemantauan Sistem</span>
                    <h3>Log Penghantaran E-mel</h3>
                    <p>Senarai e-mel automatik sistem yang berjaya dan gagal dihantar, beserta rujukan kes.</p>
                </div>
            </div>

            <div className="app-card app-complaints-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
                {[
                    { key: 'total', label: 'Jumlah', value: summary.total, color: '#0f2a63' },
                    { key: 'sent', label: 'Berjaya', value: summary.sent, color: '#1a7f45' },
                    { key: 'failed', label: 'Gagal', value: summary.failed, color: '#b02a2a' },
                    { key: 'skipped', label: 'Dilangkau', value: summary.skipped, color: '#8a6400' },
                ].map((chip) => (
                    <div key={chip.key} style={{ flex: '1 1 150px', minWidth: 140, padding: '14px 18px', borderRadius: 12, background: '#f7f9fc', border: '1px solid #e9edf3' }}>
                        <div style={{ fontSize: 12, color: '#5b6572', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{chip.label}</div>
                        <div style={{ fontSize: 26, fontWeight: 700, color: chip.color, fontVariantNumeric: 'tabular-nums' }}>{chip.value}</div>
                    </div>
                ))}
            </div>

            <div className="app-card app-complaints-card">
                <div className="app-filter-row app-filter-row-advanced">
                    <label className="app-form-field app-filter-input-advanced">
                        <span>Keyword</span>
                        <input
                            value={keyword}
                            onChange={(event) => setKeyword(event.target.value)}
                            placeholder="Cari rujukan kes, subjek, penerima, ralat..."
                        />
                    </label>
                    <label className="app-form-field">
                        <span>Status</span>
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="">Semua</option>
                            <option value="sent">Berjaya</option>
                            <option value="failed">Gagal</option>
                            <option value="skipped">Dilangkau</option>
                        </select>
                    </label>
                    <label className="app-form-field">
                        <span>Modul</span>
                        <select value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)}>
                            <option value="">Semua</option>
                            {moduleOptions.map((item) => (
                                <option key={item} value={item}>{moduleLabel(item)}</option>
                            ))}
                        </select>
                    </label>
                    <label className="app-form-field">
                        <span>Dari Tarikh</span>
                        <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                    </label>
                    <label className="app-form-field">
                        <span>Hingga Tarikh</span>
                        <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                    </label>
                    <div className="app-filter-actions-main">
                        <button type="button" className="app-button app-button-ghost" onClick={loadLogs}>
                            <i className="bi bi-arrow-clockwise"></i>
                            Refresh
                        </button>
                        <button type="button" className="app-button app-button-ghost" onClick={clearFilters}>Reset</button>
                        <button type="button" className="app-button" onClick={() => { setPage(1); loadLogs(); }}>Search</button>
                    </div>
                </div>
                {error && <div className="app-form-error">{error}</div>}
            </div>

            <div className="app-card app-complaints-card">
                {isLoading ? (
                    <div className="app-empty">Memuatkan log e-mel...</div>
                ) : (
                    <div className="app-table">
                        <SortableHeader
                            className="app-table-header app-email-log-header"
                            columns={sortColumns}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onSort={handleSort}
                        />
                        {rows.length === 0 ? (
                            <div className="app-empty">Tiada rekod e-mel ditemui.</div>
                        ) : (
                            sortedRows.map((item) => (
                                <div key={item.id} className="app-table-row app-email-log-row">
                                    <div>{item.created_at_human || '-'}</div>
                                    <div>{moduleLabel(item.module)}</div>
                                    <div>{statusBadge(item.status)}</div>
                                    <div>{item.reference_no || '-'}</div>
                                    <div style={{ wordBreak: 'break-word' }}>{item.to || '-'}</div>
                                    <div style={{ wordBreak: 'break-word' }}>{item.subject || '-'}</div>
                                    <div>
                                        <button type="button" className="app-link" onClick={() => setSelected(item)}>
                                            Detail
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {!isLoading && rows.length > 0 && (
                <PaginationBar
                    page={page}
                    lastPage={lastPage}
                    total={total}
                    perPage={perPage}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onPageChange={setPage}
                    onPerPageChange={(value) => {
                        setPerPage(value);
                        setPage(1);
                    }}
                />
            )}

            {selected && (
                <div className="app-modal">
                    <div className="app-modal-backdrop" onClick={() => setSelected(null)}></div>
                    <div className="app-modal-content">
                        <div className="app-modal-header">
                            <div>
                                <h4>Detail E-mel #{selected.id}</h4>
                                <p>{selected.created_at_human} | {moduleLabel(selected.module)} | {STATUS_LABELS[selected.status] || selected.status}</p>
                            </div>
                            <button type="button" className="app-modal-close" onClick={() => setSelected(null)}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="app-audit-detail-grid">
                            <div><strong>Status:</strong> {statusBadge(selected.status)}</div>
                            <div><strong>Modul:</strong> {moduleLabel(selected.module)}</div>
                            <div><strong>Rujukan Kes:</strong> {selected.reference_no || '-'}</div>
                            <div><strong>Mailer:</strong> {selected.mailer || '-'}</div>
                            <div className="app-span-full"><strong>Kepada (To):</strong> {selected.to || '-'}</div>
                            <div className="app-span-full"><strong>Salinan (CC):</strong> {selected.cc || '-'}</div>
                            <div className="app-span-full"><strong>Subjek:</strong> {selected.subject || '-'}</div>
                            <div className="app-span-full"><strong>Rekod Berkaitan:</strong> {selected.related_type ? `${selected.related_type} #${selected.related_id}` : '-'}</div>
                        </div>
                        {selected.error && (
                            <div className="app-audit-json-wrap">
                                <div className="app-audit-json-card">
                                    <h5>Ralat</h5>
                                    <pre>{selected.error}</pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailDeliveryLog;
